import React, { useState } from "react";
import Layout from "./Layout";
import { paymentSubmit } from "../redux/apis/pageAPI";
import { isValidCard } from "../utils/validation";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";


export default function Payment() {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [cardType, setCardType] = useState("Visa");
  const [cardNo, setCardNo] = useState("");
  const [cardDate, setCardDate] = useState("");
  const [cardCVV, setCardCVV] = useState("");
  const [cardError, setCardError] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const handleAmountChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/[^\d.]/g, "");
    const parts = inputValue.split(".");
    if (parts.length > 2) {
      inputValue = `${parts[0]}.${parts.slice(1).join("")}`;
    }
    const [integerPart, decimalPart] = inputValue.split(".");
    const cleaned =
      decimalPart !== undefined
        ? `${integerPart}.${decimalPart.slice(0, 2)}`
        : integerPart;
    setInvoiceAmount(cleaned);
  };

  const handlePhoneChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/[^\d-]/g, "");
    inputValue = inputValue.slice(0, 15);
    setPhoneNumber(inputValue);
  };

  const handleCardNoChange = (e) => {
    let inputValue = e.target.value;
    // clear stale card-related error messages when user edits the card number
    if (submitStatus === "error" && (submitMessage === "Card number is invalid" || submitMessage === cardError)) {
      setSubmitMessage("");
      setSubmitStatus("idle");
    }
    inputValue = inputValue.replace(/\D/g, "");
    inputValue = inputValue.slice(0, 16);
    
    // Detect card type from first digit
    if (inputValue.length === 0) {
      setCardError("");
      setCardType("Visa");
    } else {
      const firstDigit = inputValue[0];
      if (firstDigit === "4") {
        setCardType("Visa");
        setCardError("");
      } else if (firstDigit === "5") {
        setCardType("Mastercard");
        setCardError("");
      } else if (firstDigit === "3") {
        setCardError("We do not accept American Express");
      } else {
        setCardError("");
      }
    }
    
    if (inputValue.length > 0) {
      inputValue = inputValue.match(/.{1,4}/g).join(" ");
    }
    setCardNo(inputValue);
  };

  const handleCardDateChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/\D/g, "");
    if (inputValue.length > 2) {
      inputValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2, 4)}`;
    }
    setCardDate(inputValue);
  };

  const handleCardCVVChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/\D/g, "");
    inputValue = inputValue.slice(0, 3);
    setCardCVV(inputValue);
  };

  const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value);

  const getFieldErrorClass = (fieldName) => {
    if (submitStatus !== "error") return "";
    const errorMessages = {
      invoiceNumber: "Invoice Number is required",
      firstName: "First name is required",
      lastName: "Last name is required",
      invoiceAmount: "Invoice amount is required",
      phoneNumber: "Phone number is required",
      emailAddress: "Email address is invalid",
      cardNumber: "Card number is invalid",
      cardDate: "Card expiry date is required",
      cardCVV: "Card CVV is required",
    };

    const messages = errorMessages[fieldName];
    return submitMessage === messages ? "border-red-500" : "";
  };

  const submitPayment = async () => {
    if (!invoiceNumber.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("Invoice Number is required");
      return;
    }
    if (!firstName.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("First name is required");
      return;
    }
    if (!lastName.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("Last name is required");
      return;
    }
    if (!invoiceAmount.trim() || Number(invoiceAmount) <= 0) {
      setSubmitStatus("error");
      setSubmitMessage("Invoice amount is required");
      return;
    }
    if (!phoneNumber.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("Phone number is required");
      return;
    }
    if (!emailAddress.trim() || !isValidEmail(emailAddress.trim())) {
      setSubmitStatus("error");
      setSubmitMessage("Email address is invalid");
      return;
    }
    if (cardError) {
      setSubmitStatus("error");
      setSubmitMessage(cardError);
      return;
    }
    const isContactRcOption =
      cardType === "Contact RC for other payment options";

    // Debug: log card values to console to help diagnose persistent invalid card errors
    try {
      const cleanedCard = (cardNo || "").replace(/\s/g, "");
    } catch (e) {
      // ignore
    }

    if (!isContactRcOption && !isValidCard(cardNo)) {
      setSubmitStatus("error");
      setSubmitMessage("Card number is invalid");
      return;
    }
    if (!isContactRcOption && !cardDate.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("Card expiry date is required");
      return;
    }
    if (!isContactRcOption && !cardCVV.trim()) {
      setSubmitStatus("error");
      setSubmitMessage("Card CVV is required");
      return;
    }

    try {
      setSubmitStatus("loading");
      setSubmitMessage("");

      const payload = {
        invoiceNumber: invoiceNumber.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        businessName: businessName.trim(),
        invoiceAmount: Number(invoiceAmount),
        phoneNumber: phoneNumber.trim(),
        emailAddress: emailAddress.trim(),
        cardType,
        cardNo,
        cardDate,
        cardCVV,
      };

      const res = await paymentSubmit(payload);

      if (res?.msg === "ok") {
        setSubmitStatus("success");
        setSubmitMessage("Invoice details submitted successfully.\nThe payment receipt will be emailed after your credit card has been processed.");
        // Clear all form inputs
        setInvoiceNumber("");
        setFirstName("");
        setLastName("");
        setBusinessName("");
        setInvoiceAmount("");
        setPhoneNumber("");
        setEmailAddress("");
        setCardType("Visa");
        setCardNo("");
        setCardDate("");
        setCardCVV("");
        setCardError("");
      } else {
        setSubmitStatus("error");
        setSubmitMessage("Unable to submit invoice details.");
      }
    } catch (e) {
      setSubmitStatus("error");
      setSubmitMessage("Unable to submit invoice details.");
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto checkout-padding-left mt-10 px-8 pt-8 lg:mt-0">
        <p className="text-xl font-medium mb-4">Payment</p>
        <div className="text-sm leading-6 text-gray-700 mb-6">
          <p>Kindly submit your payment, and once it’s done, we will receive your information and move forward with the next steps.</p>
        </div>

        <div className="mb-6">
          <div className="relative pb-2">
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className={`w-full rounded-md border px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("invoiceNumber")} ${getFieldErrorClass("invoiceNumber") ? "border-red-500" : "border-gray-200"}`}
              placeholder="Invoice Number"
            />
          </div>

          <div className="flex gap-2 py-2">
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={`w-1/2 rounded-md border px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("firstName") ? "border-red-500" : "border-gray-200"}`}
              placeholder="First Name"
            />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={`w-1/2 rounded-md border px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("lastName") ? "border-red-500" : "border-gray-200"}`}
              placeholder="Last Name"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Business Name (Optional)"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <div className="w-1/2 relative">
              <span className="absolute left-4 top-3 text-sm text-gray-600">$</span>
              <input
                type="text"
                value={invoiceAmount}
                onChange={(e) => handleAmountChange(e)}
                className={`w-full rounded-md border px-4 py-3 pl-8 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("invoiceAmount") ? "border-red-500" : "border-gray-200"}`}
                placeholder="Invoice Amount"
              />
            </div>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => handlePhoneChange(e)}
              className={`w-1/2 rounded-md border px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("phoneNumber") ? "border-red-500" : "border-gray-200"}`}
              placeholder="Phone Number"
            />
          </div>

          <div className="pt-2">
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              className={`w-full rounded-md border px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("emailAddress") ? "border-red-500" : "border-gray-200"}`}
              placeholder="Email Address"
            />
          </div>

          <p className="text-xl font-medium mb-4 mt-6">Payment Information</p>
          <div className="flex flex-col">
            <select
              onChange={(e) => setCardType(e.target.value)}
              value={cardType}
              type="text"
              className="flex-1 rounded-md border border-gray-200 px-4 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="Visa">Visa</option>
              <option value="Mastercard">Mastercard</option>
              {/* <option value="Contact RC for other payment options">Contact RC for other payment options</option> */}
            </select>
            {cardType !== "Contact RC for other payment options" && (
              <div className="flex mt-3 gap-2">
                <div className="relative w-5/12 flex-shrink-0">
                  <input
                    type="text"
                    onChange={(e) => handleCardNoChange(e)}
                    value={cardNo}
                    name="card-no"
                    className={`w-full rounded-md border px-2 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("cardNumber") ? "border-red-500" : "border-gray-200"}`}
                    placeholder="xxxx xxxx xxxx xxxx"
                  />
                </div>
                <input
                  onChange={(e) => handleCardDateChange(e)}
                  value={cardDate}
                  type="text"
                  name="credit-expiry"
                  className={`w-full rounded-md border px-2 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("cardDate") ? "border-red-500" : "border-gray-200"}`}
                  placeholder="MM/YY"
                />
                <input
                  onChange={(e) => handleCardCVVChange(e)}
                  value={cardCVV}
                  type="text"
                  name="credit-cvc"
                  className={`w-full rounded-md border px-2 py-3 text-sm shadow-sm outline-none focus:z-10 focus:border-indigo-500 focus:ring-indigo-500 ${getFieldErrorClass("cardCVV") ? "border-red-500" : "border-gray-200"}`}
                  placeholder="CVV"
                />
              </div>
            )}
            {cardError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3">
                <div className="flex items-start gap-2">
                  <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
                  <p className="text-base font-semibold text-red-600">{cardError}</p>
                </div>
              </div>
            )}
            {cardType === "Contact RC for other payment options" && (
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  Please contact Running Count for other payment options and proceed with your payment.
                </p>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={submitPayment}
          disabled={submitStatus === "loading"}
          className={`mb-4 flex w-full items-center justify-center rounded-md px-8 py-3 text-base font-medium text-white ${
            submitStatus === "loading"
              ? "bg-gray-500 cursor-not-allowed"
              : "cursor-pointer border border-transparent bg-indigo-600 hover:bg-white hover:text-indigo-500 hover:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          }`}
        >
          {submitStatus === "loading" ? "Submitting..." : "Submit"}
        </button>

        {submitStatus === "success" && (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4">
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-700" aria-hidden="true" />
              <p className="text-base font-semibold text-green-700">{submitMessage}</p>
            </div>
          </div>
        )}
        {submitStatus === "error" && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-2">
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-700" aria-hidden="true" />
              <p className="text-base font-semibold text-red-700">{submitMessage}</p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}